import { Fragment, useEffect, useRef, useState, useId, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { firstName } from '../lib/format';
import { api, ApiError, apiStream } from '../lib/api';
import { useBoard } from './board';
import { useAuthOptional } from '../lib/auth';
import { ChatWidget, type Widget } from './widgets';
import { backFor } from './tabbar';
import { MarkGlyph } from './logo';

// The assistant, always on the right (as Bridge's Skipper). Its thread follows the page: a campaign's own thread on a
// campaign, the workspace thread (Home) elsewhere. Threads are shared by the team, so messages are signed. What it
// changes shows inline with an Undo; links are buttons the person clicks; its suggested answers stay in the
// conversation (the chosen one marked); long jobs show the time left. Support (the button at the top right, or Contact
// support under any chat) is answered by Outlevo's AI support, which can escalate to the Outlevo team.

type Choices = { options: string[]; fill?: { key: string; title: string } };
type LinkBtn = { path: string; label: string };
type Msg = {
  id: string; role: 'user' | 'assistant' | 'staff' | 'event'; body: string; author: string | null; mine?: boolean; at: string;
  choices?: Choices; links?: LinkBtn[]; widgets?: Widget[]; card?: string | null; focus?: { key: string; title: string }; undoable?: boolean; undone?: boolean; source?: string; link?: string;
  /** an event that makes the question before it stale (a website read failed): its suggested answers end */
  ends_question?: boolean;
  attachment?: { id: string };
  /** written by Outlevo's AI support agent (the Support thread) */
  ai?: boolean;
};
type Approval = { id: string; type: string; label: string; can_approve: boolean; approver_roles: string[] };
/** Support only: who answers (src/support/escalation.ts), and the escalation to the Outlevo team while one is open. */
export type SupportInfo = { note: string; escalation: { category: string; label: string; since: string; answered: boolean } | null };
/** The line under the Support composer: AI answers; no person and no time is promised. */
export const SUPPORT_NOTE = "Answered by Outlevo's AI support. It can escalate to the team.";
const AI_AUTHOR = 'Outlevo support (AI)';
/** The Support chip that asks for the team: sent as the person's message, so AI support escalates it. */
const ASK_TEAM = 'Ask a human from the Outlevo team';
type Thread = { chat: { id: string; status: string; with_team: boolean; subject?: string; title?: string | null; brief?: { title: string; completed?: boolean } | null; answering?: boolean } | null; assistant: boolean; approvals?: Approval[]; messages: Msg[]; support?: SupportInfo; resume?: { waiting: boolean; ready: boolean } };

const OPEN_KEY = 'olv_assistant_open';
// open by default on wide screens (it sits beside the page); on narrow ones it covers the page, so it waits to be asked
// on phones the chat covers the page, so it is never remembered open: each page starts with the page
const phone = () => typeof window !== 'undefined' && window.innerWidth <= 800;
const readOpen = () => {
  if (phone()) return false;
  try { const v = localStorage.getItem(OPEN_KEY); if (v !== null) return v === '1'; } catch { /* private mode */ }
  return typeof window !== 'undefined' && window.innerWidth >= 1100;
};
const ROLE_NAME: Record<string, string> = { sales_approver: 'a sales approver', marketing_approver: 'a marketing approver', owner: 'an owner', admin: 'an admin' };

/** Inline: **bold** and `code` only; React escapes the rest. */
function Inline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return <>{parts.map((p, i) => p.startsWith('**') && p.endsWith('**') && p.length > 4 ? <b key={i}>{p.slice(2, -2)}</b>
    : p.startsWith('`') && p.endsWith('`') && p.length > 2 ? <code key={i}>{p.slice(1, -1)}</code> : <Fragment key={i}>{p}</Fragment>)}</>;
}

/** A fenced code block with a Copy button (live 2026-10-30: code came out as one paragraph nobody could copy). */
function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="as-code">
      <button type="button" className="link small" onClick={() => { void navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}>{copied ? 'Copied' : 'Copy'}</button>
      <pre><code>{code}</code></pre>
    </div>
  );
}

/**
 * The assistant's text as blocks: fenced code, tables, lists, headings and paragraphs, each with inline bold and code.
 * Built from React elements only: the text is never inserted as HTML.
 */
export function Rich({ text }: { text: string }) {
  const lines = text.replace(/\r/g, '').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  const cells = (l: string) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
  while (i < lines.length) {
    const l = lines[i]!;
    if (/^\s*```/.test(l)) {
      const body: string[] = [];
      for (i++; i < lines.length && !/^\s*```/.test(lines[i]!); i++) body.push(lines[i]!);
      i++;
      out.push(<CodeBlock key={out.length} code={body.join('\n')} />);
      continue;
    }
    if (/^\s*\|.*\|\s*$/.test(l) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1]!)) {
      const head = cells(l); const rows: string[][] = [];
      for (i += 2; i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i]!); i++) rows.push(cells(lines[i]!));
      out.push(<div key={out.length} className="table-wrap"><table className="table as-table"><thead><tr>{head.map((h, j) => <th key={j}><Inline text={h} /></th>)}</tr></thead>
        <tbody>{rows.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j}><Inline text={c} /></td>)}</tr>)}</tbody></table></div>);
      continue;
    }
    if (/^\s*([-*•]|\d+[.)])\s+/.test(l)) {
      const ordered = /^\s*\d+[.)]\s+/.test(l); const items: string[] = [];
      for (; i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i]!); i++) items.push(lines[i]!.replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
      const List = ordered ? 'ol' : 'ul';
      out.push(<List key={out.length} className="as-list">{items.map((t, k) => <li key={k}><Inline text={t} /></li>)}</List>);
      continue;
    }
    if (/^\s*#{1,4}\s+/.test(l)) { out.push(<p key={out.length}><b><Inline text={l.replace(/^\s*#{1,4}\s+/, '').replace(/\*\*/g, '')} /></b></p>); i++; continue; }
    if (!l.trim()) { i++; continue; }
    const para: string[] = [];
    for (; i < lines.length && lines[i]!.trim() && !/^\s*(```|\||[-*•]\s|\d+[.)]\s|#{1,4}\s)/.test(lines[i]!); i++) para.push(lines[i]!);
    if (!para.length) { para.push(l); i++; }
    out.push(<p key={out.length}>{para.map((t, k) => <Fragment key={k}>{k ? <br /> : null}<Inline text={t} /></Fragment>)}</p>);
  }
  return <>{out}</>;
}

const SUGGESTIONS = ['Read my website', 'Set my markets', 'Draft my ICP', 'How do I start a campaign?'];
const campaignOf = (path: string) => path.match(/^\/campaigns\/([0-9a-f-]{36})/)?.[1] ?? null;
const chatOf = (path: string) => path.match(/^\/chat\/([0-9a-f-]{36})/)?.[1] ?? null;

/** Questions about the page the person is on, offered as chips (the assistant is told what the page shows). */
const ID = '[0-9a-f-]{36}';
const PAGE_CHIPS: Array<[RegExp, string[]]> = [
  [new RegExp(`^/approvals/${ID}`), ['Explain this in plain words', 'What changed from the last version?', 'Who needs to sign it?']],
  [new RegExp(`^/accounts/${ID}`), ['Why is it qualified or not?', 'Find contacts here']],
  [new RegExp(`^/icp/versions/${ID}`), ['Explain this ICP', 'What changed since the approved one?']],
  [new RegExp(`^/import/${ID}`), ['What’s wrong with my data?']],
  [new RegExp(`^/competitors/${ID}`), ['How do we compare?']],
  [/^\/replies\/?$/, ['Sort these']],
  [/^\/billing\/?$/, ['Where did my credits go?']],
  [/^\/kill-switches\/?$/, ['What is stopped and why?']],
  [/^\/accounts\/?$/, ['Which accounts should I work on first?']],
];

/**
 * `waitForAsk`: start closed and open only when the person asks (Home in the new experience has its own big input).
 * `pinned`: the chat is part of the screen (chat, campaign, workspace and inbox views): always open, no hide button.
 * `phoneMain`: a thread page on a phone: the chat is the screen (always open), its cards a sheet below it.
 */
export function AssistantPanel({ waitForAsk = false, pinned = false, phoneMain = false }: { waitForAsk?: boolean; pinned?: boolean; phoneMain?: boolean }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const qc = useQueryClient();
  const nav = useNavigate();
  const session = useAuthOptional()?.session;
  const myName = firstName(session?.user.email) || 'You';
  const loc = useLocation();
  const [open, setOpen] = useState(() => (waitForAsk ? false : readOpen()));
  const [thread, setThread] = useState<Thread | null>(null);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState<string | null>(null);
  const [streamLinks, setStreamLinks] = useState<LinkBtn[]>([]);
  const [streamWidgets, setStreamWidgets] = useState<Widget[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [showChoices, setShowChoices] = useState(true);
  // a screenshot attached to the next Support message (uploaded already; sent with the message)
  const [shot, setShot] = useState<{ id: string; name: string } | null>(null);
  const [shotBusy, setShotBusy] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const nudged = useRef(new Set<string>());
  // Support (the button at the top right): the workspace's conversation with Outlevo support, over this thread
  const [support, setSupport] = useState(false);
  const campaign = support ? null : campaignOf(loc.pathname);
  const chatId = support ? null : chatOf(loc.pathname); // a chat started from Home: its own thread
  const subjectKey = support ? 'support' : campaign ?? chatId ?? 'workspace';
  const page = `${document.title.replace(/^Outlevo · /, '')} (${loc.pathname})`;
  const board = useBoard(campaign, open);
  // the thread on screen now: an answer still streaming for another thread keeps going on the server but leaves this one alone
  const here = useRef(subjectKey); here.current = subjectKey;
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const withSubject = <T extends object>(b: T) => (support ? { ...b, support: true } : campaign ? { ...b, campaign } : chatId ? { ...b, thread: chatId } : b);

  const persistOpen = (v: boolean) => { setOpen(v); if (!waitForAsk && !phone()) { try { localStorage.setItem(OPEN_KEY, v ? '1' : '0'); } catch { /* private mode */ } } };
  useEffect(() => { const f = () => persistOpen(true); window.addEventListener('olv-open-support', f); return () => window.removeEventListener('olv-open-support', f); }, []);
  useEffect(() => { const f = () => { setSupport(true); persistOpen(true); }; window.addEventListener('olv-support', f); return () => window.removeEventListener('olv-support', f); }, []);
  // a card filled on the left shows in the thread at once
  const loadRef = useRef<() => Promise<unknown>>(async () => undefined);
  useEffect(() => { const f = () => void loadRef.current().catch(() => undefined); window.addEventListener('olv-thread-changed', f); return () => window.removeEventListener('olv-thread-changed', f); }, []);
  // leaving the page leaves Support
  useEffect(() => { setSupport(new URLSearchParams(loc.search).get('support') === '1'); if (new URLSearchParams(loc.search).get('support') === '1') persistOpen(true); }, [loc.pathname, loc.search]);
  useEffect(() => { document.documentElement.classList.toggle('assistant-open', open); }, [open]);
  useEffect(() => { if (waitForAsk) setOpen(false); }, [waitForAsk]);
  const wide = () => typeof window === 'undefined' || window.innerWidth > 800;
  useEffect(() => { if (pinned && wide()) setOpen(true); }, [pinned]);
  // a phone: a thread page opens the chat as the screen; leaving it closes the chat (other pages show the page)
  useEffect(() => { if (phoneMain) setOpen(true); else if (phone()) setOpen(false); }, [phoneMain]);
  // other pages may open or close it
  useEffect(() => { const f = (e: Event) => persistOpen((e as CustomEvent<boolean>).detail); window.addEventListener('olv-assistant-show', f); return () => window.removeEventListener('olv-assistant-show', f); }, []);

  const load = async () => { loadRef.current = load; const mine = subjectKey; const t = await api<Thread>(`/portal/assistant${support ? '?support=true' : campaign ? `?campaign=${campaign}` : chatId ? `?thread=${chatId}` : ''}`); if (here.current === mine) { setThread(t); setLoadErr(null); } return t; };
  const onEvent = (event: string, data: any) => {
    if (event === 'delta') { setStreaming((s) => (s ?? '') + data.text); setStatus(null); }
    if (event === 'status') setStatus(data.text);
    if (event === 'focus' && data?.card) window.dispatchEvent(new CustomEvent('olv-board-focus', { detail: data.card }));
    if (event === 'choices') setShowChoices(true);
    if (event === 'brief') void qc.invalidateQueries(); // the brief's cards on the left fill as the assistant works
    if (event === 'link' && data?.path) setStreamLinks((l) => [...l, data]);
    if (event === 'widget' && data?.type) setStreamWidgets((l) => [...l, data]);
    if (event === 'event') {
      setThread((t) => (t ? { ...t, messages: [...t.messages, { id: data.id ?? `e${Date.now()}`, role: 'event', body: data.text, author: null, at: new Date().toISOString(), card: data.card, undoable: data.undoable, source: 'assistant' }] } : t));
      void qc.invalidateQueries(); // the page on the left shows what changed, now
    }
    if (event === 'navigate' && data?.path) nav(data.path); // only when the person asked to go there
    if (event === 'done' && data?.state) { setThread(data.state); setStreaming(null); }
  };
  const abort = useRef<AbortController | null>(null);
  const stream = async (path: string, body: unknown) => {
    const mine = subjectKey; const ours = () => here.current === mine;
    setBusy(true); setNotice(null); setStreaming(''); setStatus(null); setStreamLinks([]); setStreamWidgets([]);
    const ac = new AbortController(); abort.current = ac;
    // a stream that ends without its done event was cut (a dropped connection, a release): reload what was saved
    let ended = false; let json = false; let stale = false;
    const dropped = 'The connection dropped before the answer finished. Whatever was saved is shown; ask again if anything is missing.';
    try {
      const out = await apiStream(path, body, (event, data) => {
        if (event === 'done') { ended = true; if (data?.error) stale = true; }
        if (ours()) onEvent(event, data);
      }, ac.signal);
      if (out && !out.state && out.messages) { json = true; if (ours()) setThread(out); } // a JSON answer: with the team, or nothing to say
    } catch (e) {
      if (!ours()) return;
      if (!ac.signal.aborted) setNotice(e instanceof ApiError ? e.message : dropped);
      if (e instanceof ApiError && e.code === 'CHAT_CLOSED') void load().catch(() => undefined);
    } finally {
      if (abort.current === ac) abort.current = null;
      if (!ours()) return; // eslint-disable-line no-unsafe-finally
      setBusy(false); setStreaming(null); setStatus(null); setStreamLinks([]); setStreamWidgets([]); void qc.invalidateQueries();
      if (!ended && !json && !ac.signal.aborted) setNotice((n) => n ?? dropped);
      // after cancel, a cut stream or a failed refresh: show what the server kept
      if (ac.signal.aborted || stale || (!ended && !json)) setTimeout(() => void load().catch(() => undefined), 600);
    }
  };
  // the cards on the left show the assistant at work while it answers
  useEffect(() => { window.dispatchEvent(new CustomEvent('olv-assistant-busy', { detail: busy })); }, [busy]);

  // opening, or moving to another thread (another campaign): load it; an empty one gets the assistant's opening
  useEffect(() => {
    if (!open) return;
    setThread(null); setBusy(false); setStreaming(null); setStatus(null); setStreamLinks([]); setStreamWidgets([]); setNotice(null);
    void load().then((t) => {
      if (!chatId && !support && !nudged.current.has(subjectKey) && t.assistant && !t.messages.length) { nudged.current.add(subjectKey); void stream('/portal/assistant/nudge', withSubject({ page })); }
    }).catch((e) => setLoadErr((e as Error).message));
  }, [open, subjectKey]);
  // the Outlevo team (an escalated Support conversation), or teammates writing: their messages arrive within seconds;
  // F35: while the chat waits on running work (the website read, the check of the companies) it looks every 8 s
  // an answer still being written on the server (the person left and came back): look every 4 s until it lands
  const waiting = Boolean(thread?.resume?.waiting);
  const answering = Boolean(thread?.chat?.answering) && !busy;
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => { if (!busy) void load().catch(() => undefined); }, answering ? 4000 : thread?.chat?.with_team || waiting ? 8000 : 30000);
    return () => clearInterval(t);
  }, [open, subjectKey, thread?.chat?.with_team, waiting, answering, busy]);
  // the work it waited on has ended: the assistant carries on by itself (the server lets it once per piece of work)
  // at most once a minute per chat, so a failing call can never loop
  const resumedAt = useRef(0);
  useEffect(() => {
    if (!open || busy || answering || !chatId || !thread?.resume?.ready || Date.now() - resumedAt.current < 60_000) return;
    resumedAt.current = Date.now();
    void stream('/portal/assistant/resume', withSubject({ page }));
  }, [open, busy, chatId, thread?.resume?.ready]);
  useEffect(() => { const el = list.current; if (el) el.scrollTop = el.scrollHeight; }, [thread?.messages.length, streaming]);
  // the card the latest answer is about: highlighted and opened on the left, when the chat opens and after each answer
  const lastFocus = [...(thread?.messages ?? [])].reverse().find((m) => m.role === 'assistant' && m.focus)?.focus?.key;
  useEffect(() => {
    if (!lastFocus || !pinned) return;
    const t = setTimeout(() => window.dispatchEvent(new CustomEvent('olv-board-focus', { detail: lastFocus })), 150);
    return () => clearTimeout(t);
  }, [lastFocus, subjectKey]);

  const send = async (text: string, kind?: string) => {
    const message = text.trim();
    if (!message) return;
    if (busy || answering) { setDraft(message); setNotice('One moment: I am still answering. Your message is kept in the box; send it when I am done.'); return; }
    if (thread?.chat?.status === 'closed') { setDraft(message); setNotice('This chat is closed. Reopen it to continue.'); return; }
    nudged.current.add(subjectKey); // the person spoke first: no opening greeting on top of it
    setDraft('');
    const attachment = support ? shot?.id : undefined;
    setShot(null);
    setThread((t) => ({ chat: t?.chat ?? null, assistant: t?.assistant ?? true, approvals: t?.approvals, support: t?.support, messages: [...(t?.messages ?? []), { id: `u${Date.now()}`, role: 'user', body: message, author: null, mine: true, at: new Date().toISOString(), ...(attachment ? { attachment: { id: attachment } } : {}) }] }));
    await stream('/portal/assistant/messages', withSubject({ message, page, ...(attachment ? { attachment } : {}), ...(kind && chatId ? { kind } : {}) }));
  };
  // the page asks ("Ask the assistant" on a card, Home's input): open and send
  const sendRef = useRef(send); sendRef.current = send;
  useEffect(() => {
    const f = (e: Event) => {
      const d = (e as CustomEvent<string | { text: string; kind?: string }>).detail;
      persistOpen(true);
      setTimeout(() => void (typeof d === 'string' ? sendRef.current(d) : sendRef.current(d.text, d.kind)), 0);
    };
    window.addEventListener('olv-assistant-ask', f);
    return () => window.removeEventListener('olv-assistant-ask', f);
  }, []);
  // Contact support, from any chat: the workspace's Support thread opens (noting this page and a link to this chat)
  const contactSupport = async () => {
    setNotice(null);
    try {
      const t = await api<Thread>('/portal/assistant/handoff', { method: 'POST', body: withSubject({ page }) });
      if (support) setThread(t); else setSupport(true); // switching to Support loads it
    } catch (e) { setNotice((e as Error).message); }
  };
  // a screenshot: the file goes from the browser to private storage through a signed URL, then rides on the next message
  const attach = async (f: File) => {
    setNotice(null);
    if (f.size > 5 * 1024 * 1024) { setNotice('Screenshots are limited to 5 MB.'); return; }
    setShotBusy(true);
    try {
      const a = await api<{ id: string; upload: { method: 'PUT'; url: string; headers: Record<string, string> } }>('/portal/support/attachments', { method: 'POST', body: { content_type: f.type } });
      const put = await fetch(a.upload.url, { method: 'PUT', body: f, headers: a.upload.headers });
      if (!put.ok) throw new Error('The screenshot did not upload; try again');
      setShot({ id: a.id, name: f.name });
    } catch (e) { setNotice((e as Error).message); } finally { setShotBusy(false); }
  };
  const viewShot = async (id: string) => {
    setNotice(null);
    try { const r = await api<{ url: string }>(`/portal/support/attachments/${id}`); window.open(r.url, '_blank', 'noopener'); } catch (e) { setNotice((e as Error).message); }
  };
  const restart = async () => { setNotice(null); try { setThread(await api<Thread>('/portal/assistant/restart', { method: 'POST', body: withSubject({}) })); nudged.current.delete(subjectKey); } catch (e) { setNotice((e as Error).message); } };
  const reopen = async () => {
    setNotice(null);
    try { await api(`/portal/threads/${thread!.chat!.id}/reopen`, { method: 'POST' }); await load(); void qc.invalidateQueries(); } catch (e) { setNotice((e as Error).message); }
  };
  const undo = async (m: Msg) => {
    setNotice(null);
    try { await api(`/portal/assistant/events/${m.id}/undo`, { method: 'POST' }); await load(); void qc.invalidateQueries(); } catch (e) { setNotice((e as Error).message); }
  };

  if (!open && !(pinned && wide()) && !phoneMain) {
    return (
      <button type="button" className="assistant-launch" onClick={() => persistOpen(true)} aria-label="Open the assistant">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /></svg>
        <span>Assistant</span>
      </button>
    );
  }
  const escalation = support ? thread?.support?.escalation ?? null : null;
  const closed = thread?.chat?.status === 'closed';
  const finished = !closed && Boolean(thread?.chat?.brief?.completed);
  const continueChat = async () => {
    setNotice(null);
    try { setThread(await api<Thread>('/portal/assistant/continue', { method: 'POST', body: { thread: chatId } })); void qc.invalidateQueries(); } catch (e) { setNotice((e as Error).message); }
  };
  // the "↳ card" line points at cards on the left, so only where the page shows them; an approval page gets its own questions
  const cardsHere = pinned && /^\/(chat|campaigns\/)/.test(loc.pathname); // Inbox and All work are pinned too, but show no cards
  const pageChips = PAGE_CHIPS.find(([re]) => re.test(loc.pathname))?.[1] ?? null;
  const msgs = thread?.messages ?? [];
  // the latest message's suggested answers stay live until someone answers (an alert or a change in between does not
  // end the question); earlier ones stay in the conversation, the chosen one marked
  const lastIdx = msgs.map((m) => m.role !== 'event').lastIndexOf(true);
  const last = msgs[lastIdx];
  const stale = msgs.slice(lastIdx + 1).some((m) => m.ends_question);
  const live = !busy && !finished && !closed && !stale && last?.role === 'assistant' && (last.choices?.options.length || last.choices?.fill) ? last.choices.options : null;
  const fill = !busy && !finished && !closed && !stale && last?.role === 'assistant' ? last.choices?.fill : undefined;
  // fill a card yourself: it opens on the left for editing
  // on a phone the cards are in the sheet under the chat: open it
  const toCards = () => { if (phone()) window.dispatchEvent(new CustomEvent('olv-cards-sheet', { detail: 'full' })); };
  const fillCard = (key: string) => { toCards(); window.dispatchEvent(new CustomEvent('olv-board-focus', { detail: key })); window.dispatchEvent(new CustomEvent('olv-brief-edit', { detail: key })); };
  const where = phone() ? 'below' : 'on the left';
  const chosen = (i: number) => msgs.slice(i + 1).find((m) => m.role === 'user')?.body;
  const working = board.data?.working ?? [];
  const title = support ? 'Outlevo Support' : campaign ? (thread?.chat?.title ?? 'This campaign') : chatId ? (thread?.chat?.title ?? 'New chat') : 'Workspace';
  return (
    <aside className="assistant" aria-label="Assistant">
      {/* no header: the page says what the chat is. Support keeps a thin line with the way back. */}
      {support ? (
        <div className="as-topline"><b>Outlevo Support</b><span className="muted small"><span className="ai-badge">AI</span> answers at once · can escalate to the team</span><button type="button" className="link small as-back" onClick={() => setSupport(false)}>← Back to the chat</button></div>
      ) : null}
      {/* a phone shows the chat over the page: say which chat it is, with a way back */}
      {phone() && !support ? (phoneMain
        ? <div className="as-topline as-phone-head main"><Link className="link small as-phone-back" to={backFor(loc.pathname).to}>← {backFor(loc.pathname).label}</Link><b>{title}</b></div>
        : <div className="as-topline as-phone-head"><b>{title}</b><button type="button" className="link small" onClick={() => persistOpen(false)}>Close</button></div>) : null}
      {pinned && wide() || phone() ? null : (
        <button type="button" className="icon-btn as-hide" onClick={() => persistOpen(false)} aria-label="Hide the assistant" title="Hide">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
        </button>
      )}
      <div className="as-list" ref={list} aria-live="polite">
        {thread && !support ? (
          <div className="as-msg r-assistant as-welcome">
            <div className="as-who"><Avatar kind="outlevo" />Outlevo</div>
            <div className="as-bubble">{chatId
              ? `Setting up a new chat${thread.chat?.brief?.title ? `: ${thread.chat.brief.title}` : ''}. I'll fill the cards ${where} with you, one at a time.`
              : campaign ? `This is the “${thread.chat?.title ?? 'campaign'}” thread. The cards ${where} show where the campaign stands; I'll work through them with you.`
              : 'This is your workspace thread: setting Outlevo up, and questions across campaigns.'}</div>
          </div>
        ) : null}
        {(thread?.approvals ?? []).map((a) => (
          <div key={a.id} className="as-approval" role="group" aria-label={`Approval: ${a.label}`}>
            <b>Approval needed</b><span>{a.label}</span>
            {a.can_approve ? <Link className="btn btn-primary" to={`/approvals/${a.id}`}>Review and approve</Link>
              : <small>Waiting for {a.approver_roles.map((r) => ROLE_NAME[r] ?? r).join(' or ')}; they were notified.</small>}
          </div>
        ))}
        {msgs.map((m, i) => {
          if (m.role === 'event') {
            const kind = m.source === 'alert' ? 'alert' : m.source === 'user' ? 'by-user' : '';
            return (
              <div key={m.id} className={`as-event ${kind}${m.undone ? ' undone' : ''}`}>
                <span aria-hidden="true">{m.source === 'alert' ? '!' : m.source === 'user' ? '✎' : '✓'}</span>
                <span className="as-event-text">{m.source === 'user' && m.author ? `${m.mine ? 'You' : m.author}: ` : ''}{m.body}</span>
                {m.link ? <Link className="link" to={m.link}>Open</Link> : null}
                {m.undoable ? <button type="button" className="link" onClick={() => void undo(m)} aria-label={`Undo: ${m.body}`}>Undo</button> : null}
                {m.undone ? <small>undone</small> : null}
              </div>
            );
          }
          const isLive = i === lastIdx && Boolean(live);
          const pick = chosen(i);
          return (
            <div key={m.id} className={`as-msg r-${m.role}${m.role === 'user' && m.mine === false ? ' other' : ''}${m.role === 'assistant' && i === lastIdx && !busy ? ' asking' : ''}`}>
              <Who m={m} me={myName} ai={support} />
              <div className="as-bubble"><Rich text={m.body} /></div>
              {m.attachment && !m.id.startsWith('u') ? <button type="button" className="link small as-shot" onClick={() => void viewShot(m.attachment!.id)}>View the screenshot</button> : null}
              {m.role === 'assistant' && m.focus && cardsHere ? (
                <button type="button" className="as-about" onClick={() => { toCards(); window.dispatchEvent(new CustomEvent('olv-board-focus', { detail: m.focus!.key })); }} title={`Show this card ${where}`}>
                  <span aria-hidden="true">↳</span> {m.focus.title}
                </button>
              ) : null}
              {m.widgets?.map((w, wi) => chatId
                ? <div key={wi} className="wg-ref"><span aria-hidden="true">←</span> {w.title}{w.done ? ` · ✓ ${w.done.text}` : ''}</div>
                : <ChatWidget key={wi} w={w} messageId={m.id} index={wi} onChange={() => void load()} />)}
              {m.links?.length ? <div className="as-links">{m.links.map((l) => <Link key={l.path} className="as-link" to={l.path}>{l.label}</Link>)}</div> : null}
              {m.choices?.options.length && !isLive ? (
                <ul className="as-past-choices" aria-label="Suggested answers (earlier)">
                  {m.choices.options.map((o) => <li key={o} className={pick === o ? 'chosen' : ''}>{pick === o ? '✓ ' : ''}{o}</li>)}
                </ul>
              ) : null}
            </div>
          );
        })}
        {answering && streaming === null ? <div className="as-status" role="status">Still working on the last answer…</div> : null}
        {streaming !== null ? (
          <div className="as-msg r-assistant">
            <div className="as-who"><Avatar kind="outlevo" busy={busy} />{support ? <>{AI_AUTHOR}<span className="ai-badge">AI</span></> : 'Outlevo'}{busy ? <span className="typing" aria-hidden="true"><i /><i /><i /></span> : null}
              {busy ? <button type="button" className="link small as-cancel" onClick={() => { void api('/portal/assistant/cancel', { method: 'POST', body: withSubject({}) }).catch(() => undefined); abort.current?.abort(); }}>cancel</button> : null}</div>
            {status || !streaming ? <div className="as-status">{status ?? 'Thinking…'}</div> : null}
            {streaming ? <div className="as-bubble"><Rich text={streaming} /></div> : null}
            {streamWidgets.map((w, wi) => <ChatWidget key={wi} w={w} index={wi} onChange={() => undefined} />)}
            {streamLinks.length ? <div className="as-links">{streamLinks.map((l) => <Link key={l.path} className="as-link" to={l.path}>{l.label}</Link>)}</div> : null}
          </div>
        ) : null}
        {!busy && !answering && streaming === null && last?.role === 'user' ? (
          <div className="as-retry"><span className="muted small">No answer came.</span><button type="button" className="link small" onClick={() => void stream('/portal/assistant/retry', withSubject({ page }))}>Ask again</button></div>
        ) : null}
        {thread && !msgs.length && streaming === null ? <p className="as-empty">{support ? 'Hi! I am Outlevo\'s AI support. Ask anything about Outlevo: how something works, billing, an error. I check your workspace and answer right away, and I can escalate to the Outlevo team.' : 'Ask anything about Outlevo, or let me set your workspace up with you.'}</p> : null}
      </div>
      {working.length ? (
        <div className="as-progress" aria-live="polite">
          <div className="as-progress-head"><b>Working on it</b><span>{working.length} running · estimates</span></div>
          {working.map((w) => (
            <div key={w.key} className="as-progress-row">
              <span className="spin" aria-hidden="true" /><span>{w.label}</span><span className="muted">{w.eta}</span>
              <div className="bd-bar"><i style={{ width: `${w.progress}%` }} /></div>
            </div>
          ))}
        </div>
      ) : null}
      {live || fill ? (
        showChoices ? (
          <div className="as-choices rows" role="group" aria-label="Suggested answers">
            <button type="button" className="as-choices-x" onClick={() => setShowChoices(false)} aria-label="Hide the options">×</button>
            {(live ?? []).map((o, i) => <button key={o} type="button" className={i === 0 ? 'first' : ''} onClick={() => void send(o)}><span className="radio" aria-hidden="true" />{o}</button>)}
            {fill ? <button type="button" className="as-fill" onClick={() => fillCard(fill.key)}><span aria-hidden="true">✎</span>Fill “{fill.title}” myself, {where}</button> : null}
          </div>
        ) : <button type="button" className="as-see" onClick={() => setShowChoices(true)}>See {(live ?? []).length} option{(live ?? []).length === 1 ? '' : 's'}</button>
      ) : pageChips && !support ? (
        <div className="as-chips">{pageChips.map((s) => <button key={s} type="button" disabled={busy} onClick={() => void send(s)}>{s}</button>)}</div>
      ) : support && !escalation ? (
        <div className="as-chips">{(msgs.length < 2 ? ['How do credits work?', 'Something is not working', ASK_TEAM] : [ASK_TEAM]).map((s) => <button key={s} type="button" disabled={busy} onClick={() => void send(s)}>{s}</button>)}</div>
      ) : !support && msgs.length < 3 && !campaign && !chatId ? (
        <div className="as-chips">{SUGGESTIONS.map((s) => <button key={s} type="button" disabled={busy} onClick={() => void send(s)}>{s}</button>)}</div>
      ) : null}
      {notice ? <p className="as-notice" role="alert">{notice}</p> : null}
      {loadErr && !thread ? <p className="as-notice" role="alert">The chat could not be loaded: {loadErr} <button type="button" className="link" onClick={() => { setLoadErr(null); void load().catch((e) => setLoadErr((e as Error).message)); }}>Try again</button></p> : null}
      {closed ? (
        <div className="as-closed" role="status"><span>This chat is closed. It stays here to read.</span><button type="button" className="btn btn-sm" onClick={() => void reopen()}>Reopen</button></div>
      ) : finished ? (
        <div className="as-closed as-finished" role="status"><span>✓ This chat is complete.</span>
          <span className="as-closed-actions"><button type="button" className="btn btn-sm btn-primary" onClick={() => nav('/')}>Go to the main dashboard</button><button type="button" className="btn btn-sm" onClick={() => void continueChat()}>Continue this chat</button></span>
        </div>
      ) : <form className={`as-compose${live && showChoices ? ' answer' : ''}`} onSubmit={(e) => { e.preventDefault(); void send(draft); }}>
        {live && showChoices ? <span className="radio as-compose-dot" aria-hidden="true" /> : null}
        <textarea id={`${uid}-f1`} rows={2} maxLength={4000} value={draft} placeholder={live ? 'Something else? Type it here…' : support ? 'Ask, or describe the problem' : 'Ask me anything'} aria-label="Message"
          onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(draft); } }} />
        <button type="submit" className="as-send" disabled={busy || !draft.trim()} aria-label="Send">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
        </button>
      </form>}
      {support && !closed ? (
        <div className="as-support-line">
          <label className={`link small as-attach${shotBusy ? ' busy' : ''}`}>{shotBusy ? 'Uploading…' : shot ? 'Change the screenshot' : 'Attach a screenshot'}
            <input id={`${uid}-shot`} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="sr-only" disabled={shotBusy} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void attach(f); }} /></label>
          {shot ? <span className="as-shot-chip">{shot.name}<button type="button" className="link small" onClick={() => setShot(null)} aria-label={`Remove ${shot.name}`}>×</button></span> : null}
          {/* who answers (the AI), and while escalated, that the team has it: no time is promised */}
          <p className="as-target" role="status">{escalation ? <><b>{escalation.answered ? 'The Outlevo team replied; they reply by email and here.' : 'With the Outlevo team: they reply by email and here.'}</b> </> : null}{thread?.support?.note ?? SUPPORT_NOTE}</p>
        </div>
      ) : null}
      <div className="as-foot">
        {support ? <button type="button" className="link" onClick={() => void restart()}>Start a new conversation</button> : <button type="button" className="link" onClick={() => void contactSupport()}>Contact support</button>}
        <span>{support ? 'AI support can make mistakes. It reads your workspace and changes nothing in it.' : 'The assistant can make mistakes; it never approves or sends. Everything it changes can be undone.'}</span>
      </div>
    </aside>
  );
}

/** Who wrote a message: the Outlevo mark for the assistant, a person's initial, a team badge for the Outlevo team. */
function Avatar({ kind, name, busy }: { kind: 'outlevo' | 'me' | 'person' | 'staff'; name?: string; busy?: boolean }) {
  if (kind === 'outlevo') return <span className={`av av-outlevo${busy ? ' busy' : ''}`} aria-hidden="true"><MarkGlyph /></span>;
  return <span className={`av av-${kind}`} aria-hidden="true">{(name ?? '?').slice(0, 1).toUpperCase()}</span>;
}
function Who({ m, me, ai }: { m: Msg; me: string; ai?: boolean }) {
  // Support: the AI agent signs as AI, with a badge, never with a name
  if (m.role === 'assistant') return m.ai || ai ? <div className="as-who"><Avatar kind="outlevo" />{AI_AUTHOR}<span className="ai-badge">AI</span></div> : <div className="as-who"><Avatar kind="outlevo" />Outlevo</div>;
  if (m.role === 'staff') return <div className="as-who"><Avatar kind="staff" name="O" />Outlevo team</div>;
  if (m.role === 'user' && m.mine === false) return <div className="as-who"><Avatar kind="person" name={m.author ?? '?'} />{m.author ?? 'A teammate'}</div>;
  return <div className="as-who mine">{me}<Avatar kind="me" name={me} /></div>;
}
